import { formatTime, isValidTime } from "@personeel/utils/DateTimeUtils.ts";
import { usePersoneelsdienstenStore } from "@personeel/stores/PersoneelsdienstenStore";
import { startPersoneelsdienstSchedulerPolling } from "@personeel/hooks/usePersoneelsdienstSchedulerPolling";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import { DATETIME_FORMAT_NO_SECONDS } from "@shared/common/utils/luxonHelpers";
import { useMessageBannerStore } from "@shared/shared-zustand/MessageBannerStore";
import { usePersoneelsdienstScheduler } from "@personeel/hooks/useFetchData";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { useQueryClient } from "@tanstack/react-query";
import { loadDefaultFeedbackMessages } from "@tractie/components";
import { DateTime } from "luxon";
import { ChangeEvent, JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./PersoneelsdienstScheduler.css";

type DienstOptie = {
  id: string;
  label: string;
  starttijd: DateTime;
  eindtijd: DateTime;
};

type CheckboxElement = HTMLElement & {
  checked: boolean;
};

type ModelValueChangedDetail = {
  isTriggeredByUser?: boolean;
};

export function PersoneelsdienstScheduler(): JSX.Element {
  const scenarioInterval = useSelectedTimeStore((state) => state.scenarioInterval)!;
  const scenarioStart = scenarioInterval.start;
  const scenarioEnd = scenarioInterval.end;

  const personeelsdiensten = usePersoneelsdienstenStore((state) => state.personeelsdiensten);
  const setMessageBanner = useMessageBannerStore((state) => state.setBanner);
  const { knopClient } = useKnopClient();
  const queryClient = useQueryClient();
  const instanceId = useCurrentInstanceId((state) => state.instanceId);
  const { mutateAsync: startPersoneelsdienstScheduler, isPending } = usePersoneelsdienstScheduler();

  const [errors, setErrors] = useState("");
  const isSubmittingRef = useRef(false);
  const pollingPeriodRef = useRef("");

  const [formState, setFormState] = useState({
    start: scenarioStart.toFormat("yyyy-MM-dd"),
    startTime: scenarioStart.toFormat("HH:mm"),
    end: scenarioEnd.toFormat("yyyy-MM-dd"),
    endTime: scenarioEnd.toFormat("HH:mm"),
  });

  const dienstOpties = useMemo<DienstOptie[]>(() => {
    return personeelsdiensten.flatMap((dienstGroep) => {
      return dienstGroep.personeelsdiensten.map((dienst) => ({
        id: dienst.id,
        label: dienst.label.replace(" - ", " "),
        starttijd: dienst.starttijd,
        eindtijd: dienst.eindtijd,
      }));
    });
  }, [personeelsdiensten]);

  const geselecteerdePeriode = useMemo(() => {
    const start = DateTime.fromFormat(
      `${formState.start} ${formState.startTime}`,
      "yyyy-MM-dd HH:mm",
    );
    const eind = DateTime.fromFormat(`${formState.end} ${formState.endTime}`, "yyyy-MM-dd HH:mm");
    return { start, eind };
  }, [formState.end, formState.endTime, formState.start, formState.startTime]);

  const isDienstGeldigVoorPeriode = useCallback(
    (dienst: DienstOptie) => {
      const { start, eind } = geselecteerdePeriode;
      if (!start.isValid || !eind.isValid || start > eind) {
        return false;
      }

      // Dienst is selecteerbaar als deze overlap heeft met de gekozen periode.
      return dienst.starttijd <= eind && dienst.eindtijd >= start;
    },
    [geselecteerdePeriode],
  );

  const selecteerbareDienstOpties = useMemo(
    () => dienstOpties.filter(isDienstGeldigVoorPeriode),
    [dienstOpties, isDienstGeldigVoorPeriode],
  );

  const selecteerbareDienstIds = useMemo(
    () => new Set(selecteerbareDienstOpties.map((dienst) => dienst.id)),
    [selecteerbareDienstOpties],
  );

  const [selectedDienstIds, setSelectedDienstIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedDienstIds(new Set(dienstOpties.map((dienst) => dienst.id)));
  }, [dienstOpties]);

  useEffect(() => {
    setSelectedDienstIds((prev) => {
      return new Set(Array.from(prev).filter((dienstId) => selecteerbareDienstIds.has(dienstId)));
    });
  }, [selecteerbareDienstIds]);

  const updateFormState = useCallback((data: Partial<typeof formState>) => {
    setFormState((previousState) => ({ ...previousState, ...data }));
  }, []);

  const isValidTimeInput = useCallback(
    (time: string, name: "startTime" | "endTime"): string => {
      const formattedTime = formatTime(time.trim());

      if (formattedTime === "" || !isValidTime(formattedTime)) {
        updateFormState({ [name]: time });
        return "invalid";
      }

      updateFormState({ [name]: formattedTime });
      return formattedTime;
    },
    [updateFormState],
  );

  const validateInput = useCallback(
    (allValues: typeof formState): string => {
      let errorMessage = "";

      const startDate = allValues["start"];
      const startTime = isValidTimeInput(allValues["startTime"], "startTime");
      const endDate = allValues["end"];
      const endTime = isValidTimeInput(allValues["endTime"], "endTime");

      if (startTime === "invalid" || endTime === "invalid") {
        errorMessage = "Ongeldige tijd ingevoerd.";
      } else {
        const selectedScenarioStartDateTime = DateTime.fromFormat(
          `${startDate} ${startTime}`,
          "yyyy-MM-dd HH:mm",
        );
        const selectedScenarioEndDateTime = DateTime.fromFormat(
          `${endDate} ${endTime}`,
          "yyyy-MM-dd HH:mm",
        );

        if (
          selectedScenarioStartDateTime < scenarioStart ||
          selectedScenarioEndDateTime > scenarioEnd
        ) {
          errorMessage =
            "De begindatum/tijd en de einddatum/tijd moeten binnen het scenariobereik vallen.";
        } else if (selectedScenarioStartDateTime > selectedScenarioEndDateTime) {
          errorMessage = "De startdatum/tijd moet voor de einddatum/tijd liggen.";
        }
      }

      setErrors(errorMessage);
      return errorMessage;
    },
    [isValidTimeInput, scenarioEnd, scenarioStart],
  );

  const closeDialog = (event: CustomEvent<unknown>) => {
    (event.target as unknown as { close: () => void }).close();
  };

  const resetToScenarioDefaults = useCallback(() => {
    setErrors("");
    updateFormState({
      start: scenarioStart.toFormat("yyyy-MM-dd"),
      startTime: scenarioStart.toFormat("HH:mm"),
      end: scenarioEnd.toFormat("yyyy-MM-dd"),
      endTime: scenarioEnd.toFormat("HH:mm"),
    });
    setSelectedDienstIds(new Set(dienstOpties.map((dienst) => dienst.id)));
  }, [dienstOpties, scenarioEnd, scenarioStart, updateFormState]);

  const submitSchedulerJob = useCallback(
    async (event: CustomEvent<unknown>) => {
      event.stopPropagation();

      if (isSubmittingRef.current || isPending) {
        return;
      }

      const errorMessage = validateInput(formState);
      if (errorMessage) {
        return;
      }

      if (selectedDienstIds.size === 0) {
        setErrors("Selecteer minimaal een dienst.");
        return;
      }

      isSubmittingRef.current = true;

      const startDateTime = DateTime.fromFormat(
        `${formState.start} ${formState.startTime}`,
        "yyyy-MM-dd HH:mm",
      );

      const endDateTime = DateTime.fromFormat(
        `${formState.end} ${formState.endTime}`,
        "yyyy-MM-dd HH:mm",
      );

      if (!startDateTime.isValid || !endDateTime.isValid) {
        setErrors("Kon de ingevoerde datum/tijd niet verwerken.");
        isSubmittingRef.current = false;
        return;
      }

      const dienstIds = Array.from(selectedDienstIds).filter((dienstId) =>
        selecteerbareDienstIds.has(dienstId),
      );

      if (dienstIds.length === 0) {
        setErrors("Selecteer minimaal een geldige dienst binnen de gekozen periode.");
        isSubmittingRef.current = false;
        return;
      }

      const formatPeriodDate = (dt: DateTime) => dt.setLocale("nl").toFormat("ccc d MMM HH:mm");
      pollingPeriodRef.current = `${formatPeriodDate(startDateTime)} tot en met ${formatPeriodDate(endDateTime)}`;

      closeDialog(event);

      try {
        const mutationResponse = await startPersoneelsdienstScheduler({
          start: startDateTime.toISO(),
          eind: endDateTime.toISO(),
          dienstIds,
        });

        startPersoneelsdienstSchedulerPolling({
          ticket: mutationResponse.ticket,
          startedAtMs: Date.now(),
          periodLabel: pollingPeriodRef.current,
          knopClient,
          queryClient,
          instanceId: instanceId ?? "",
        });
      } catch {
        setMessageBanner("Automatisch toewijzen is mislukt. Probeer het later opnieuw.", "error");
      } finally {
        isSubmittingRef.current = false;
      }
    },
    [
      formState,
      instanceId,
      knopClient,
      queryClient,
      selectedDienstIds,
      selecteerbareDienstIds,
      setMessageBanner,
      startPersoneelsdienstScheduler,
      isPending,
      validateInput,
    ],
  );

  const handleSubmit: (event: CustomEvent<unknown>) => void = useCallback(
    (event: CustomEvent<unknown>) => {
      void submitSchedulerJob(event);
    },
    [submitSchedulerJob],
  );

  const handleCancel = useCallback(
    (event: CustomEvent<unknown>) => {
      resetToScenarioDefaults();
      closeDialog(event);
    },
    [resetToScenarioDefaults],
  );

  useEffect(() => {
    loadDefaultFeedbackMessages();
  }, []);

  const scenarioDateRange = useMemo(() => {
    const range: Record<string, string> = {};
    let current = scenarioStart;

    while (current <= scenarioEnd) {
      range[current.toFormat("yyyy-MM-dd")] = current.toLocaleString({
        weekday: "short",
        month: "2-digit",
        day: "2-digit",
      });
      current = current.plus({ days: 1 }).startOf("day");
    }

    return range;
  }, [scenarioEnd, scenarioStart]);

  // Specifieke handlers die alleen het bedoelde veld bijwerken.
  const handleDateChange = useCallback(
    (name: "start" | "end") => (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setErrors("");
      setFormState((prev) => {
        const updated = { ...prev, [name]: value };
        validateInput(updated);
        return updated;
      });
    },
    [validateInput],
  );

  const handleTimeChange = useCallback(
    (name: "startTime" | "endTime") => (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value ?? "";
      setErrors("");
      setFormState((prev) => {
        const updated = { ...prev, [name]: value };
        validateInput(updated);
        return updated;
      });
    },
    [validateInput],
  );

  const isAllDienstenSelected =
    selecteerbareDienstOpties.length > 0 &&
    selectedDienstIds.size === selecteerbareDienstOpties.length;

  const handleAllDienstenChange = useCallback(
    (event: CustomEvent<never>) => {
      // Ignore programmatic sync events; only user interaction should change all selections.
      const details = event.detail as ModelValueChangedDetail | undefined;
      if (!details?.isTriggeredByUser) {
        return;
      }

      const checkbox = event.currentTarget as CheckboxElement | null;
      if (!checkbox) {
        return;
      }

      setErrors("");
      setSelectedDienstIds(
        checkbox.checked
          ? new Set(selecteerbareDienstOpties.map((dienst) => dienst.id))
          : new Set(),
      );
    },
    [selecteerbareDienstOpties],
  );

  const handleDienstChange = useCallback((dienstId: string, event: CustomEvent<never>) => {
    const checkbox = event.currentTarget as CheckboxElement | null;
    if (!checkbox) {
      return;
    }

    setErrors("");
    setSelectedDienstIds((currentSelectedDienstIds) => {
      const updatedDienstIds = new Set(currentSelectedDienstIds);

      if (checkbox.checked) {
        updatedDienstIds.add(dienstId);
      } else {
        updatedDienstIds.delete(dienstId);
      }

      return updatedDienstIds;
    });
  }, []);

  return (
    <tractie-dialog
      heading={"Automatisch toewijzen klussen"}
      size={"m"}
      ondialog-close={handleCancel}
      ondialog-cancel={handleCancel}
      ondialog-submit={handleSubmit}
      buttons={{
        cancel: {
          label: "Annuleren",
        },
        submit: {
          label: "Start automatisch toewijzen",
        },
      }}
    >
      <tractie-button data-testid="ps-scheduler-button" slot="invoker" level="secondary">
        <tractie-icon name="plus" />
        Automatisch toewijzen
      </tractie-button>

      <tractie-divider />
      <div className="ps-dialog-formgroup">
        Kies een periode binnen het scenario {" ("}
        {scenarioStart.toFormat(DATETIME_FORMAT_NO_SECONDS)}
        {" - "}
        {scenarioEnd.toFormat(DATETIME_FORMAT_NO_SECONDS)}
        {")"}
      </div>
      {isPending ? (
        <div>Bezig met automatisch toewijzen...</div>
      ) : (
        <tractie-form name={"personeelsdienstSchedulerDialoog"}>
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="form-time-selector">
              <tractie-select
                id={"start"}
                label={"Start"}
                className="ps-dialog-date"
                modelValue={formState.start}
                name="startouter"
              >
                <select
                  name="start"
                  slot="input"
                  value={formState.start}
                  onChange={handleDateChange("start")}
                >
                  {Object.entries(scenarioDateRange).map(([dateValue, label]) => (
                    <option key={dateValue} value={dateValue}>
                      {label}
                    </option>
                  ))}
                </select>
              </tractie-select>
              <tractie-input
                name="startTime"
                id={"startTime"}
                label=" "
                modelValue={formState.startTime}
                placeholder="00:00"
                className="ps-dialog-date"
                onChange={handleTimeChange("startTime")}
              />
              <tractie-select
                id={"end"}
                label={"Eind"}
                className="ps-dialog-date"
                modelValue={formState.end}
                name="endouter"
              >
                <select
                  name="end"
                  slot="input"
                  value={formState.end}
                  onChange={handleDateChange("end")}
                >
                  {Object.entries(scenarioDateRange).map(([dateValue, label]) => (
                    <option key={dateValue} value={dateValue}>
                      {label}
                    </option>
                  ))}
                </select>
              </tractie-select>
              <tractie-input
                id={"endTime"}
                name={"endTime"}
                label={" "}
                modelValue={formState.endTime}
                placeholder="00:00"
                className="ps-dialog-date"
                onChange={handleTimeChange("endTime")}
              />
            </div>

            <div className="ps-error-container">
              {errors && <tractie-error-message>{errors}</tractie-error-message>}
            </div>

            <div className="ps-dialog-services">
              <tractie-label>Diensten</tractie-label>
              <div className="ps-checkbox-row">
                <tractie-checkbox
                  id="all-diensten"
                  label={""}
                  checked={isAllDienstenSelected}
                  onmodel-value-changed={handleAllDienstenChange}
                />
                <span className="ps-checkbox-label">Alle diensten</span>
              </div>

              {selecteerbareDienstOpties.map((dienst) => (
                <div key={dienst.id} className="ps-checkbox-row">
                  <tractie-checkbox
                    id={`dienst-${dienst.id}`}
                    name={`dienst-${dienst.id}`}
                    label={""}
                    checked={selectedDienstIds.has(dienst.id)}
                    onmodel-value-changed={(event) => handleDienstChange(dienst.id, event)}
                  />
                  <span className="ps-checkbox-label">{dienst.label}</span>
                </div>
              ))}

              {selecteerbareDienstOpties.length === 0 && (
                <div>Er zijn geen diensten geldig binnen de gekozen periode.</div>
              )}
            </div>

            <tractie-divider />
          </form>
        </tractie-form>
      )}
    </tractie-dialog>
  );
}
